import React from 'react'
import AntdTooltip from 'antd/es/tooltip'
import type { TooltipProps, TooltipRef } from 'antd/es/tooltip'

import { useDesignMode } from 'src/design-mode/useDesignMode'

import { Tooltip as ShadcnSeamTooltip } from './Tooltip.shadcn'

/**
 * Compat seam leaf for antd's `antd/es/tooltip` — and, with `../Modal`, the
 * FIFTH and SIXTH components put behind the design toggle, after `./Button`
 * (#970), `./Typography` + `./Space` (#971) and `./Tag` (#974).
 *
 * A LEAF, not the `src/antd` barrel, on purpose (PRO-130): the barrel's
 * `export * from 'antd'` pulls antd/es/index.js — the whole component barrel —
 * into whatever chunk imports it, and this module is reached from the
 * boot-static `./Typography` seam. `update-design` ships a real seam at this
 * same path, so a call site pointed here needs no further change when that
 * lands.
 *
 * ---------------------------------------------------------------------------
 * THE SWITCH, and its safety property
 *
 * `useDesignMode()` returns `'antd'` for everyone who has not picked "New
 * design" under Theme — which is the shipped default, so it is every page load
 * of every customer who has not opted in. In the `'antd'` branch this component renders antd's own
 * Tooltip with the props it was given and the ref it was given: same element,
 * same class names, same DOM. The only difference from
 * `export { default as Tooltip } from 'antd/es/tooltip'` — which is what this
 * file was — is one extra component in the React tree, which renders nothing
 * of its own. `__tests__/tooltip-seam-parity.test.tsx` asserts that across a
 * prop matrix, and it is the test to keep green.
 *
 * WHY TOOLTIP AND MODAL TOGETHER. Both are PORTAL surfaces: they render
 * outside their parent's DOM position and depend on stacking, z-index and
 * container choice. Porting them in one pass means ONE set of decisions about
 * where overlays mount rather than two lanes each inventing their own. That
 * decision — body portal, never the trigger's parent — is written out in
 * `./Tooltip.shadcn.tsx`, and `../Modal/Modal.shadcn.tsx` points at it.
 *
 * ONE THING THAT IS NOT PORTED, and is worth knowing before you read a
 * mixed screen: `src/modules/invoicing/_shared/externalBilling.tsx` imports
 * `antd/es/tooltip` directly (a documented carve-out — the seam barrel drags
 * the storybook re-exports into every locked invoicing surface's test graph).
 * Its tooltips stay antd in BOTH modes. The residual walk in
 * `__tests__/seam-adoption.evidence.test.tsx` already counts that file.
 */
const TooltipDispatcher = React.forwardRef<TooltipRef, TooltipProps>(
  (props, ref) => {
    const designMode = useDesignMode()

    if (designMode === 'shadcn') {
      // antd's `TooltipRef` exposes `forceAlign()` + the underlying popup
      // element. Radix re-positions on its own and exposes neither, so the
      // shadcn branch takes no ref. No call site in this repo takes a ref to a
      // Tooltip; the dispatcher still declares one so the antd branch's own
      // shape is unchanged.
      return <ShadcnSeamTooltip {...props} />
    }

    return <AntdTooltip {...props} ref={ref} />
  }
)

TooltipDispatcher.displayName = 'Tooltip'

export const Tooltip = TooltipDispatcher

export default TooltipDispatcher
